import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DescriptionIcon from '@mui/icons-material/Description';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import trainingApi from '../../services/trainingApi';

const DIFFICULTY_COLORS = {
  beginner: 'success',
  intermediate: 'warning',
  advanced: 'warning',
  expert: 'error',
};

const CATEGORY_LABELS = {
  mood_disorders: 'Mood Disorders',
  anxiety_disorders: 'Anxiety Disorders',
  psychotic_disorders: 'Psychotic Disorders',
  personality_disorders: 'Personality Disorders',
  personality_complex: 'Personality & Complex Presentations',
  substance_use: 'Substance Use',
  neurodevelopmental_disorders: 'Neurodevelopmental',
  trauma_and_stress: 'Trauma & Stress',
  eating_disorders: 'Eating Disorders',
  liaison_psychiatry: 'Liaison Psychiatry',
  child_and_adolescent: 'Child & Adolescent',
  older_adult: 'Older Adult',
  forensic: 'Forensic',
  specialist: 'Specialist',
  cross_cutting_skills: 'Cross-Cutting Skills',
};

const SETTING_LABELS = {
  outpatient_clinic: 'Outpatient Clinic',
  emergency_department: 'Emergency Department',
  inpatient_ward: 'Inpatient Ward',
  community_team: 'Community Team',
  gp_surgery: 'GP Surgery',
  liaison_consultation: 'Liaison Consultation',
  camhs_clinic: 'CAMHS Clinic',
  forensic_unit: 'Forensic Unit',
  residential_care: 'Residential Care',
  telehealth: 'Telehealth',
};

const ScenarioDetailsDialog = ({ open, onClose, scenarioId, onStart, onTestRun }) => {
  const [scenario, setScenario] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !scenarioId) return;

    let cancelled = false;
    const fetchScenario = async () => {
      setLoading(true);
      setError(null);
      setScenario(null);
      try {
        const response = await trainingApi.getScenario(scenarioId);
        if (cancelled) return;
        const body = response.data || response;
        const data = body.data || body;
        setScenario(data.scenario || data);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || err.message || 'Failed to load scenario details');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchScenario();
    return () => { cancelled = true; };
  }, [open, scenarioId]);

  const handleStart = () => {
    if (onStart && scenarioId) {
      onStart(scenarioId);
    }
  };

  const handleTestRun = () => {
    if (onTestRun && scenarioId) {
      onTestRun(scenarioId);
    }
  };

  const visibilityLevel = scenario?.visibilityLevel || 'full_brief';
  const isBlind = visibilityLevel === 'blind';
  const isReferralOnly = visibilityLevel === 'referral_only';
  const isFullBrief = visibilityLevel === 'full_brief';

  const context = scenario?.scenarioContext || {};
  const hasVoice = scenario?.voiceConfig?.avmEnabled || scenario?.voiceConfig?.enableTTS;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {loading ? 'Loading...' : (scenario?.name || 'Scenario Details')}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {error && <Alert severity="error">{error}</Alert>}

        {!loading && scenario && (
          <Box>
            {/* Badges row */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip
                label={scenario.difficulty}
                size="small"
                color={DIFFICULTY_COLORS[scenario.difficulty] || 'default'}
                sx={{ textTransform: 'capitalize' }}
              />
              {isBlind && (
                <Chip icon={<VisibilityOffIcon />} label="Blind Assessment" size="small" color="secondary" />
              )}
              {isReferralOnly && (
                <Chip icon={<DescriptionIcon />} label="Referral Mode" size="small" color="info" />
              )}
              {!isBlind && scenario.category && (
                <Chip label={CATEGORY_LABELS[scenario.category] || scenario.category} size="small" variant="outlined" />
              )}
              {context.timeLimit && (
                <Chip icon={<AccessTimeIcon />} label={`${context.timeLimit} min`} size="small" variant="outlined" />
              )}
              {hasVoice && (
                <Chip icon={<HeadsetMicIcon />} label="Voice Mode" size="small" variant="outlined" />
              )}
            </Box>

            {/* Content warning */}
            {context.contentWarning && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2"><strong>Content Warning:</strong> {context.contentWarning}</Typography>
              </Alert>
            )}

            {/* Blind mode notice */}
            {isBlind && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  This is a blind assessment. You will discover the clinical presentation during the session.
                  Approach the patient as you would in a real clinical encounter with no prior information.
                </Typography>
              </Alert>
            )}

            {/* Setting */}
            {context.setting && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="overline" color="text.secondary">Setting</Typography>
                <Typography variant="body1">
                  {SETTING_LABELS[context.setting] || context.setting}
                </Typography>
              </Box>
            )}

            {/* Description — full_brief only */}
            {isFullBrief && scenario.description && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="overline" color="text.secondary">Description</Typography>
                <Typography variant="body2">{scenario.description}</Typography>
              </Box>
            )}

            {/* Presenting complaint — full_brief only */}
            {isFullBrief && context.presentingComplaint && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="overline" color="text.secondary">Presenting Complaint</Typography>
                <Typography variant="body2">{context.presentingComplaint}</Typography>
              </Box>
            )}

            {/* Referral summary — referral_only mode */}
            {isReferralOnly && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="overline" color="text.secondary">Referral Information</Typography>
                <Typography variant="body2">
                  {scenario.referralSummary || context.referralSource || 'Referral details available'}
                </Typography>
              </Box>
            )}

            {/* Learning objectives — full_brief only */}
            {isFullBrief && scenario.learningObjectives?.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ mb: 2 }}>
                  <Typography variant="overline" color="text.secondary">Learning Objectives</Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {scenario.learningObjectives.map((obj, i) => (
                      <Typography component="li" variant="body2" key={i} sx={{ mb: 0.5 }}>
                        {obj}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              </>
            )}

            {/* Available assessments — full_brief only */}
            {isFullBrief && scenario.availableAssessments?.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="overline" color="text.secondary">Available Assessments</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                  {scenario.availableAssessments.map(a => (
                    <Chip key={a} label={a.toUpperCase()} size="small" variant="outlined" />
                  ))}
                </Box>
              </Box>
            )}

            {/* Time limit note */}
            {context.timeLimit && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Time limit: {context.timeLimit} minutes
                  {context.timeLimitEnforced === false && ' (advisory — you can continue beyond the limit)'}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Close</Button>
        {onTestRun ? (
          // Lecturers/admins get a Test Run (ungraded verification) rather than a
          // graded student session, which they should never start for themselves.
          <Button
            variant="contained"
            startIcon={<PlayArrowIcon />}
            onClick={handleTestRun}
            disabled={loading || !!error}
          >
            Test Run
          </Button>
        ) : (
          <Button
            variant="contained"
            startIcon={<PlayArrowIcon />}
            onClick={handleStart}
            disabled={loading || !!error}
          >
            Start Session
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ScenarioDetailsDialog;
